import { useCallback, useState } from "react";

import { PaymentsWalletsService } from "../services/paymentsWallets.service";
import type { RuntimeWallet } from "../types/payments-wallets.types";

export interface UseWalletActivationResult {
  currencies: string[];
  isLoadingCurrencies: boolean;
  isActivating: boolean;
  loadCurrencies: () => Promise<void>;
  /**
   * Activates the wallet and returns it. Throws on failure so the caller can surface the backend message.
   * `organizationId` is INTERNAL-only (activate on behalf of a client org); members omit it (server-scoped).
   */
  activate: (currency: string, organizationId?: string) => Promise<RuntimeWallet>;
}

/**
 * Wallet activation for org members: the currency catalog (`GET .../currencies`) for the selector and the
 * idempotent create (`POST /wallets`). The server scopes the create to the member's own org, so we never send
 * `organization_id` here (handoff §2, §3.2).
 */
export const useWalletActivation = (): UseWalletActivationResult => {
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const loadCurrencies = useCallback(async () => {
    setIsLoadingCurrencies(true);
    try {
      const res = await PaymentsWalletsService.listCurrencies();
      setCurrencies(res.data.currencies);
    } catch {
      setCurrencies([]);
    } finally {
      setIsLoadingCurrencies(false);
    }
  }, []);

  const activate = useCallback(async (currency: string, organizationId?: string): Promise<RuntimeWallet> => {
    setIsActivating(true);
    try {
      const res = await PaymentsWalletsService.create({
        currency,
        ...(organizationId ? { organization_id: organizationId } : {}),
      });
      return res.data;
    } finally {
      setIsActivating(false);
    }
  }, []);

  return { currencies, isLoadingCurrencies, isActivating, loadCurrencies, activate };
};
